<template>
  <section class="mail-card mail-card-editor">
    <div class="mail-card-header-row">
      <h2>{{ tt("general.content", "İçerik") }}</h2>
      <mail-code-dropdown :codes="codes"/>
    </div>
    <div class="mail-editor-frame">
      <div :class="{ 'is-invalid': contentError }"
           class="mail-editor-shell"
      >
        <summernote v-if="template"
                    :key="editorKey"
                    ref="mailEditor"
                    :config="editorConfig"
                    :model="template.html || ''"
                    :name="`mail-template-editor-${template.id}`"
                    height="430px"
                    @change="$emit('change', $event)"
        />
        <div class="mail-editor-footer">
          <div class="mail-editor-tools">
            <button :title="tt('general.undo', 'Geri al')"
                    type="button"
                    @click="runEditor('undo')"
            >
              <i class="uil-corner-up-left"></i>
            </button>
            <button :title="tt('general.redo', 'İleri al')"
                    type="button"
                    @click="runEditor('redo')"
            >
              <i class="uil-corner-up-right"></i>
            </button>
            <button :title="tt('general.preview', 'Önizleme')"
                    type="button"
                    @click="$emit('focus-preview')"
            >
              <i class="uil-eye"></i>
            </button>
            <button :title="tt('mail.source_code', 'Kaynak kod')"
                    type="button"
                    @click="runEditor('toggleCodeview')"
            >
              <span>&lt;/&gt;</span>
            </button>
          </div>
          <span class="mail-word-count">
            {{ tt("general.word_count", "Kelime") }}: {{ wordCount }}
          </span>
        </div>
      </div>
    </div>
    <p v-if="contentError" class="mail-field-error">
      {{ contentError }}
    </p>
  </section>
</template>

<script>
import MailCodeDropdown from "@/view/company/Mail/components/MailCodeDropdown.vue";

const Summernote = () =>
    import(
        /* webpackChunkName: "summernote-editor" */ "@/view/company/Mail/Summernote"
        );

export default {
  name: "MailTemplateEditorCard",
  components: { MailCodeDropdown, Summernote },
  props: {
    template: {
      type: Object,
      default: null,
    },
    editorKey: {
      type: Number,
      default: 0,
    },
    codes: {
      type: Array,
      default() {
        return [];
      },
    },
    contentError: {
      type: String,
      default: "",
    },
    wordCount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      editorConfig: {
        toolbar: [
          ["style", ["style"]],
          ["font", ["bold", "italic", "underline", "clear"]],
          ["fontname", ["fontname"]],
          ["fontsize", ["fontsize"]],
          ["para", ["ul", "ol", "paragraph"]],
          ["height", ["height"]],
          ["table", ["table"]],
          ["color", ["color"]],
          ["insert", ["link", "picture", "hr"]],
          ["view", ["codeview"]],
        ],
        disableResizeEditor: true,
        dialogsInBody: true,
      },
    };
  },
  methods: {
    tt(key, fallback) {
      if (this.$te && this.$te(key)) {
        const translated = this.$t(key);
        if (translated && translated !== key) return translated;
      }
      return fallback;
    },
    runEditor(method) {
      const editor = this.$refs.mailEditor;
      if (editor && typeof editor[method] === "function") {
        editor[method]();
      }
    },
  },
};
</script>

<style scoped>
.mail-card {
  background: #fff;
  border: 1px solid var(--mail-border);
  border-radius: 12px;
  padding: 20px 24px 24px;
  overflow: visible;
}

.mail-card-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.mail-card h2 {
  margin: 0;
  color: var(--mail-text);
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.mail-editor-frame {
  position: relative;
  overflow: visible;
}

.mail-editor-shell {
  position: relative;
  overflow: visible;
  border: 1px solid var(--mail-border);
  border-radius: 12px;
  background: #fff;
}

.mail-editor-shell.is-invalid {
  border-color: #e31748;
}

.mail-editor-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 46px;
  padding: 0 14px;
  border-top: 1px solid var(--mail-border);
  background: #fafbff;
}

.mail-editor-tools {
  display: flex;
  align-items: center;
  gap: 4px;
}

.mail-editor-tools button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: #fff;
  color: #6b7194;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 1px 2px rgba(23, 26, 47, 0.04);
}

.mail-editor-tools button:hover {
  color: var(--mail-primary);
  border-color: rgba(91, 79, 255, 0.2);
  background: #f4f2ff;
  box-shadow: 0 2px 6px rgba(91, 79, 255, 0.10);
}

.mail-editor-tools button:active {
  background: rgba(91, 79, 255, 0.12);
  box-shadow: none;
}

.mail-editor-tools button i {
  font-size: 17px;
  line-height: 1;
}

.mail-editor-tools button span {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
}

.mail-word-count {
  color: #9aa0b8;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
  background: #fff;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid var(--mail-border);
}

.mail-field-error {
  margin: 8px 0 0;
  color: #e31748;
  font-size: 12px;
}

::v-deep .note-editor.note-frame,
::v-deep .note-editor.note-frame.card {
  border: 0 !important;
  box-shadow: none !important;
  margin: 0 !important;
  background: transparent !important;
  border-radius: 0 !important;
}

::v-deep .note-editor .note-toolbar,
::v-deep .note-editor .note-toolbar.card-header,
::v-deep .note-editor .note-toolbar.panel-heading {
  display: flex !important;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 10px 14px !important;
  border: 0 !important;
  border-bottom: 1px solid var(--mail-border) !important;
  border-radius: 0 !important;
  background: #f8f9fe !important;
}

::v-deep .note-editor .note-toolbar .note-btn-group {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin: 0 !important;
  padding: 3px 4px;
  background: #fff;
  border: 1px solid #eceef6;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(23, 26, 47, 0.03);
}

::v-deep .note-editor .note-toolbar .note-btn-group + .note-btn-group::before {
  content: none;
}

::v-deep .note-editor .note-toolbar .note-btn-group .btn,
::v-deep .note-editor .note-toolbar .note-btn {
  height: 32px !important;
  min-width: 32px;
  padding: 0 8px !important;
  border: 0 !important;
  border-radius: 6px !important;
  background: transparent !important;
  box-shadow: none !important;
  color: #5c6180 !important;
  font-size: 13px !important;
  line-height: 1 !important;
  transition: all 0.18s ease;
  position: relative;
}

::v-deep .note-editor .note-toolbar .note-fontname .note-btn {
  min-width: 120px;
  padding: 0 12px !important;
  font-size: 12.5px !important;
  font-weight: 500 !important;
  text-align: left !important;
  justify-content: flex-start !important;
}

::v-deep .note-editor .note-toolbar .note-fontsize .note-btn {
  min-width: 52px;
  padding: 0 12px !important;
  font-size: 12.5px !important;
  font-weight: 500 !important;
  justify-content: center !important;
}

::v-deep .note-editor .note-toolbar .note-btn i:not(.note-recent-color) {
  color: inherit !important;
  font-size: 16px !important;
  padding: 0 !important;
  line-height: 1 !important;
}

::v-deep .note-editor .note-toolbar .note-btn i.note-recent-color {
  font-size: 16px !important;
  padding: 0 !important;
}

::v-deep .note-editor .note-toolbar .note-color .note-current-color-button i.note-icon-font {
  font-family: summernote !important;
  display: inline-block !important;
  visibility: visible !important;
  width: 18px !important;
  height: 20px !important;
  line-height: 16px !important;
  text-align: center !important;
  border-radius: 3px !important;
}


::v-deep .note-editor .note-toolbar .note-btn .note-icon-caret {
  font-size: 10px !important;
  margin-left: 2px;
  opacity: 0.5;
  transition: opacity 0.15s ease;
}

::v-deep .note-editor .note-toolbar .note-btn:hover .note-icon-caret {
  opacity: 1;
}

::v-deep .note-editor .note-toolbar .note-btn:hover {
  color: var(--mail-primary) !important;
  background: rgba(91, 79, 255, 0.08) !important;
}

::v-deep .note-editor .note-toolbar .note-btn:hover i:not(.note-recent-color) {
  color: var(--mail-primary) !important;
}

::v-deep .note-editor .note-toolbar .note-btn.active,
::v-deep .note-editor .note-toolbar .note-btn:active {
  color: var(--mail-primary) !important;
  background: rgba(91, 79, 255, 0.14) !important;
  box-shadow: none !important;
}

::v-deep .note-editor .note-toolbar .note-btn.active i:not(.note-recent-color),
::v-deep .note-editor .note-toolbar .note-btn:active i:not(.note-recent-color) {
  color: var(--mail-primary) !important;
}

::v-deep .note-editor .note-toolbar .note-btn.dropdown-toggle::after {
  content: none !important;
}

::v-deep .note-editor .note-toolbar .note-color .note-btn.dropdown-toggle {
  border: 1px solid var(--mail-border) !important;
  color: #5c6180 !important;
}

::v-deep .note-editor .note-toolbar .note-style .note-btn,
::v-deep .note-editor .note-toolbar .note-fontname .note-btn,
::v-deep .note-editor .note-toolbar .note-fontsize .note-btn {
  background: transparent !important;
}

::v-deep .note-editor .note-toolbar .note-style .note-btn:hover,
::v-deep .note-editor .note-toolbar .note-fontname .note-btn:hover,
::v-deep .note-editor .note-toolbar .note-fontsize .note-btn:hover {
  background: rgba(91, 79, 255, 0.06) !important;
  color: var(--mail-primary) !important;
}

::v-deep .note-editor .note-toolbar .note-style .note-btn.active,
::v-deep .note-editor .note-toolbar .note-style .note-btn:active,
::v-deep .note-editor .note-toolbar .note-fontname .note-btn.active,
::v-deep .note-editor .note-toolbar .note-fontname .note-btn:active,
::v-deep .note-editor .note-toolbar .note-fontsize .note-btn.active,
::v-deep .note-editor .note-toolbar .note-fontsize .note-btn:active {
  color: var(--mail-primary) !important;
  background: rgba(91, 79, 255, 0.10) !important;
  box-shadow: none !important;
}

::v-deep .note-editor .note-toolbar .dropdown-menu {
  min-width: 140px;
  padding: 6px !important;
  border: 1px solid var(--mail-border) !important;
  border-radius: 10px !important;
  background: #fff !important;
  box-shadow: 0 8px 24px rgba(23, 26, 47, 0.10),
              0 2px 6px rgba(23, 26, 47, 0.04) !important;
  animation: mailDropFadeIn 0.15s ease;
}

::v-deep .note-editor .note-toolbar .dropdown-menu .dropdown-item,
::v-deep .note-editor .note-toolbar .dropdown-menu .note-btn {
  display: flex !important;
  align-items: center !important;
  padding: 7px 12px !important;
  border-radius: 6px !important;
  color: #3a3f5c !important;
  font-size: 13px !important;
  font-weight: 400 !important;
  line-height: 1.4 !important;
  transition: background 0.12s ease, color 0.12s ease;
  white-space: nowrap;
}

::v-deep .note-editor .note-toolbar .dropdown-menu .dropdown-item:hover,
::v-deep .note-editor .note-toolbar .dropdown-menu .dropdown-item:focus,
::v-deep .note-editor .note-toolbar .dropdown-menu .note-btn:hover {
  background: #f4f2ff !important;
  color: var(--mail-primary) !important;
}

::v-deep .note-editor .note-toolbar .dropdown-menu .dropdown-item.active,
::v-deep .note-editor .note-toolbar .dropdown-menu .dropdown-item:active {
  background: rgba(91, 79, 255, 0.10) !important;
  color: var(--mail-primary) !important;
  font-weight: 600 !important;
}

::v-deep .note-editor .note-toolbar .note-style .dropdown-menu {
  min-width: 180px;
}

::v-deep .note-editor .note-toolbar .note-style .dropdown-menu blockquote,
::v-deep .note-editor .note-toolbar .note-style .dropdown-menu pre {
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  background: transparent !important;
  font-size: inherit !important;
}

::v-deep .note-editor .note-toolbar .note-para .dropdown-menu {
  min-width: auto;
  padding: 8px !important;
}

::v-deep .note-editor .note-toolbar .note-para .dropdown-menu .note-btn {
  width: 30px !important;
  height: 30px !important;
  min-width: 30px !important;
  padding: 0 !important;
  display: inline-flex !important;
  justify-content: center !important;
}

::v-deep .note-editor .note-toolbar .note-color .dropdown-menu {
  min-width: 340px;
  padding: 12px !important;
}

::v-deep .note-editor .note-toolbar .note-color .dropdown-menu .note-palette {
  display: inline-block !important;
  width: 160px !important;
  margin: 0 !important;
  vertical-align: top !important;
}

::v-deep .note-editor .note-toolbar .note-color .dropdown-menu .note-palette:first-child {
  margin-right: 10px !important;
}

::v-deep .note-editor .note-toolbar .note-color .note-palette .note-palette-title {
  margin: 0 0 6px !important;
  padding: 0 !important;
  font-size: 11px !important;
  font-weight: 600 !important;
  color: #9398ad !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

::v-deep .note-editor .note-toolbar .note-color .note-color-reset,
::v-deep .note-editor .note-toolbar .note-color button.note-color-select {
  display: block !important;
  width: 100% !important;
  height: auto !important;
  min-width: unset !important;
  padding: 7px 12px !important;
  margin: 6px 0 4px !important;
  border-radius: 6px !important;
  border: 1px solid var(--mail-border) !important;
  background: #f8f9fe !important;
  color: #3a3f5c !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  text-align: center;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
}

::v-deep .note-editor .note-toolbar .note-color .note-color-reset:hover,
::v-deep .note-editor .note-toolbar .note-color button.note-color-select:hover {
  background: #f4f2ff !important;
  border-color: var(--mail-primary) !important;
  color: var(--mail-primary) !important;
}

::v-deep .note-editor .note-toolbar .note-color input.note-color-select-btn {
  display: block !important;
  width: 100% !important;
  height: 32px !important;
  min-width: unset !important;
  padding: 2px !important;
  margin: 4px 0 !important;
  border-radius: 6px !important;
  border: 1px solid var(--mail-border) !important;
  background: #fff !important;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

::v-deep .note-editor .note-toolbar .note-color input.note-color-select-btn::-webkit-color-swatch-wrapper {
  padding: 2px;
}

::v-deep .note-editor .note-toolbar .note-color input.note-color-select-btn::-webkit-color-swatch {
  border: 0;
  border-radius: 4px;
}

::v-deep .note-editor .note-toolbar .note-color input.note-color-select-btn::-moz-color-swatch {
  border: 0;
  border-radius: 4px;
}

::v-deep .note-editor .note-toolbar .note-color input.note-color-select-btn:hover {
  border-color: var(--mail-primary) !important;
  box-shadow: 0 0 0 2px rgba(91, 79, 255, 0.1);
}

::v-deep .note-editor .note-toolbar .note-color .note-color-btn {

  transition: transform 0.1s ease, box-shadow 0.1s ease;
}



::v-deep .note-editor .note-toolbar .note-color .note-color-btn:hover {
  transform: scale(1.25);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  z-index: 1;
  position: relative;
}

::v-deep .note-editor .note-toolbar .note-table .dropdown-menu {
  min-width: auto;
  padding: 10px !important;
}

::v-deep .note-editor .note-toolbar .note-table .note-dimension-picker {
  margin: 0 auto;
}

::v-deep .note-editor .note-toolbar .note-table .note-dimension-display {
  text-align: center;
  font-size: 11px !important;
  color: #9398ad !important;
  font-weight: 500 !important;
  margin-top: 6px;
}

::v-deep .note-editor .note-toolbar .note-insert .dropdown-menu {
  min-width: 160px;
}

::v-deep .note-editor .note-toolbar .note-fontname .dropdown-menu {
  min-width: 160px;
  max-height: 280px;
  overflow-y: auto;
}

::v-deep .note-editor .note-toolbar .note-fontname .dropdown-menu::-webkit-scrollbar {
  width: 5px;
}

::v-deep .note-editor .note-toolbar .note-fontname .dropdown-menu::-webkit-scrollbar-track {
  background: transparent;
}

::v-deep .note-editor .note-toolbar .note-fontname .dropdown-menu::-webkit-scrollbar-thumb {
  background: #d4d7e3;
  border-radius: 3px;
}

::v-deep .note-editor .note-toolbar .note-fontsize .dropdown-menu {
  min-width: 80px;
  max-height: 260px;
  overflow-y: auto;
}

::v-deep .note-editor .note-toolbar .note-fontsize .dropdown-menu .dropdown-item {
  justify-content: center !important;
  padding: 6px 12px !important;
  font-variant-numeric: tabular-nums;
}

::v-deep .note-editor .note-toolbar .note-height .dropdown-menu {
  min-width: 80px;
}

::v-deep .note-editor .note-toolbar .note-height .dropdown-menu .dropdown-item {
  justify-content: center !important;
  font-variant-numeric: tabular-nums;
}

::v-deep .note-editor .note-editing-area {
  background: #fff;
  overflow: hidden;
}

::v-deep .note-editor .note-editable {
  min-height: 430px !important;
  padding: 24px 24px 20px !important;
  color: var(--mail-text) !important;
  font-size: 15px !important;
  line-height: 1.7 !important;
  height: auto !important;
}

::v-deep .note-editor .note-statusbar,
::v-deep .note-editor .note-statusbar.card-footer {
  display: none !important;
}

@media (max-width: 767px) {
  .mail-card {
    padding: 18px;
  }

  ::v-deep .note-editor .note-toolbar {
    padding-right: 12px !important;
  }

  ::v-deep .note-editor .note-editable {
    padding: 18px !important;
  }
}
</style>

<style>
.mail-template-page .note-editable img,
.mail-template-page .note-editable table {
  max-width: 100%;
  height: auto;
}

.mail-template-page .note-editable h1,
.mail-template-page .note-editable h2,
.mail-template-page .note-editable h3 {
  margin: 0 0 12px;
  color: #171a2f;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
}

.mail-template-page .note-editable p {
  margin: 0 0 12px;
}

.mail-template-page .note-editable .btn,
.mail-template-page .note-editable a.btn {
  display: inline-block;
  background: #5b4fff !important;
  color: #fff !important;
  padding: 12px 28px;
  border: 0;
  border-radius: 10px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
}

.mail-template-page .note-editor .note-toolbar .note-btn-group .btn {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
}

/* ── Color toolbar button: selected color indicator ── */
.mail-template-page .note-editor .note-toolbar .note-color .note-current-color-button i.note-icon-font {
  display: inline-block !important;
  visibility: visible !important;
  opacity: 1 !important;
  font-size: 16px !important;
  font-family: summernote !important;
  line-height: 16px !important;
  width: 18px !important;
  height: 20px !important;
  text-align: center !important;
  border-radius: 3px !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ── Color dropdown: "Select" button ── */
.mail-template-page .note-editor .note-toolbar .note-color .dropdown-menu button.note-color-select {
  display: block !important;
  width: 100% !important;
  height: auto !important;
  padding: 7px 12px !important;
  margin: 6px 0 4px !important;
  border-radius: 6px !important;
  border: 1px solid #e8eaf3 !important;
  background: #f8f9fe !important;
  color: #3a3f5c !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  text-align: center !important;
}

.mail-template-page .note-editor .note-toolbar .note-color .dropdown-menu button.note-color-select:hover {
  background: #f4f2ff !important;
  border-color: #5b4fff !important;
  color: #5b4fff !important;
}

/* ── Color dropdown: native color picker input ── */
.mail-template-page .note-editor .note-toolbar .note-color .dropdown-menu input.note-color-select-btn {
  display: block !important;
  width: 100% !important;
  height: 32px !important;
  padding: 2px !important;
  margin: 4px 0 !important;
  border-radius: 6px !important;
  border: 1px solid #e8eaf3 !important;
  background: #fff !important;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.mail-template-page .note-editor .note-toolbar .note-color .dropdown-menu input.note-color-select-btn::-webkit-color-swatch-wrapper {
  padding: 2px;
}

.mail-template-page .note-editor .note-toolbar .note-color .dropdown-menu input.note-color-select-btn::-webkit-color-swatch {
  border: 0;
  border-radius: 4px;
}

.mail-template-page .note-editor .note-toolbar .note-color .dropdown-menu input.note-color-select-btn:hover {
  border-color: #5b4fff !important;
}

.mail-template-page .note-editor .note-toolbar .note-fontname .note-btn {
  min-width: 110px;
}

.mail-template-page .note-editor .note-toolbar .note-fontsize .note-btn {
  min-width: 56px;
}

@keyframes mailDropFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.mail-template-page .note-popover.popover {
  z-index: 1060 !important;
  border: 1px solid #e8eaf3 !important;
  border-radius: 12px !important;
  box-shadow: 0 12px 32px rgba(23, 26, 47, 0.12),
              0 2px 6px rgba(23, 26, 47, 0.04) !important;
  padding: 6px 8px !important;
  background: #fff !important;
  animation: mailDropFadeIn 0.15s ease;
}

.mail-template-page .note-popover.popover .popover-body,
.mail-template-page .note-popover.popover .popover-content {
  padding: 0 !important;
}

.mail-template-page .note-popover.popover .arrow {
  display: none !important;
}

.mail-template-page .note-popover .note-btn-group {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin: 0 !important;
  padding: 2px 3px;
  background: #f8f9fe;
  border-radius: 8px;
}

.mail-template-page .note-popover .note-btn-group + .note-btn-group {
  margin-left: 4px !important;
  padding-left: 3px;
  border-left: 0;
}

.mail-template-page .note-popover .note-btn {
  height: 30px !important;
  min-width: 30px;
  padding: 0 7px !important;
  border: 0 !important;
  border-radius: 6px !important;
  background: transparent !important;
  color: #5c6180 !important;
  font-size: 12px !important;
  transition: all 0.18s ease;
}

.mail-template-page .note-popover .note-btn:hover {
  background: rgba(91, 79, 255, 0.08) !important;
  color: #5b4fff !important;
}

.mail-template-page .note-popover .note-btn.active {
  background: rgba(91, 79, 255, 0.14) !important;
  color: #5b4fff !important;
}

.mail-template-page .note-popover .note-btn i {
  font-size: 15px !important;
  color: inherit !important;
  line-height: 1;
}

.mail-template-page .note-editor .note-toolbar .dropdown-menu {
  z-index: 1060 !important;
}

.mail-template-page .note-modal {
  z-index: 1070 !important;
}

.mail-template-page .note-modal .modal-content {
  border: 1px solid #e8eaf3 !important;
  border-radius: 14px !important;
  box-shadow: 0 20px 48px rgba(23, 26, 47, 0.16),
              0 4px 12px rgba(23, 26, 47, 0.06) !important;
  overflow: hidden;
}

.mail-template-page .note-modal .modal-header {
  padding: 16px 20px !important;
  border-bottom: 1px solid #e8eaf3 !important;
  background: #f8f9fe !important;
}

.mail-template-page .note-modal .modal-header .modal-title {
  font-size: 15px !important;
  font-weight: 700 !important;
  color: #171a2f !important;
}

.mail-template-page .note-modal .modal-header .close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  opacity: 0.6;
  transition: all 0.15s ease;
}

.mail-template-page .note-modal .modal-header .close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.06);
}

.mail-template-page .note-modal .modal-body {
  padding: 20px !important;
}

.mail-template-page .note-modal .modal-body .form-group label {
  font-size: 12.5px !important;
  font-weight: 600 !important;
  color: #3a3f5c !important;
  margin-bottom: 6px !important;
}

.mail-template-page .note-modal .modal-body .form-control {
  height: 38px !important;
  padding: 6px 12px !important;
  border: 1px solid #e5e8f0 !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  color: #3a3f5c !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.mail-template-page .note-modal .modal-body .form-control:focus {
  border-color: #5b4fff !important;
  box-shadow: 0 0 0 3px rgba(91, 79, 255, 0.08) !important;
  outline: none !important;
}

.mail-template-page .note-modal .modal-footer {
  padding: 12px 20px !important;
  border-top: 1px solid #e8eaf3 !important;
  background: #fafbff !important;
}

.mail-template-page .note-modal .modal-footer .btn {
  height: 36px !important;
  padding: 0 18px !important;
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  transition: all 0.15s ease;
}

.mail-template-page .note-modal .modal-footer .btn-primary {
  background: #5b4fff !important;
  border-color: #5b4fff !important;
  color: #fff !important;
  box-shadow: 0 2px 8px rgba(91, 79, 255, 0.2);
}

.mail-template-page .note-modal .modal-footer .btn-primary:hover {
  background: #4d3fef !important;
  box-shadow: 0 4px 12px rgba(91, 79, 255, 0.3);
}

.mail-template-page .note-modal-backdrop {
  z-index: 1065 !important;
}

.note-dropdown-menu.dropdown-menu.show {
  display: flex;
}

</style>
